import { useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import TrackingAPI from 'api/TrackingAPI';
import TokenIcon from 'assets/token.svg?react';
import NiceModal from "components/modals/NiceModal";
import { UpgradeButton } from 'components/modals/SubscriptionModal/styles';
import localStorageService from 'helpers/localStorageService';
import useModalState from 'hooks/useModalState';
import BuyTokensModal from 'modals/BuyTokensModal';
import { useDispatch, useMappedState } from 'redux-react-hook';
import { analyticsService } from 'services/analyticsService';
import { useImageToVideoMutation, useLazyGetDialogByScenarioAliasQuery } from 'services/api/dialogsApi';
import { useGetTokenCostsQuery } from 'services/api/tokenApi';
import { refetchCurrentUser } from "store/DEPRECATEDusers/asyncAction";
import styled from "styled-components";
import { errorAlert } from 'ui-kit/Alert';
import { getErrorMessage } from "utils/error";

interface IConfirmVideoGenerationModalProps {
  isVisible: boolean;
  onClose: () => void;
  batchId: string;
  setBatches: (batches: unknown[]) => void;
  game?: {
    configJson?: {
      config_name?: string;
    };
    name?: string;
    publicName?: string;
  };
  scenario?: {
    configJson?: {
      scenario_name?: string;
    };
    name?: string;
    publicName?: string;
  };
}

const ConfirmVideoGenerationModal = (props: IConfirmVideoGenerationModalProps) => {
  const { batchId, isVisible, setBatches, onClose, game, scenario } = props;
  const hasTrackedRef = useRef(false);

  const [isBuyTokensModalOpen, openBuyTokensModal, closeBuyTokensModal] = useModalState(false);
  const [imageToVideo, { isLoading }] = useImageToVideoMutation();
  const [getDialogByAlias] = useLazyGetDialogByScenarioAliasQuery();
  const dispatch = useDispatch();
  const { alias } = useParams();

  const characterName = localStorageService.get('characterName') ?? '';

  const { currentUser } = useMappedState(
    useCallback(state => ({
      currentUser: state.users.usersMap.get(state.users.currentUserId),
    }), [])
  );

  const { data: tokenCosts } = useGetTokenCostsQuery(undefined, {
    selectFromResult: ({ data }) => ({ data: data ?? null })
  });

  const dialogVideoGenerationCost = tokenCosts?.dialogVideoGenerationCost ?? 0;
  const tokenBalance = currentUser?.tokenBalance ?? 0.0;

  useEffect(() => {
    const trackEvent = () => {
      if (hasTrackedRef.current) return;
      hasTrackedRef.current = true;
      try {
        TrackingAPI.trackEvent('video_generation_modal_open', {
          rendered_for_reason: 'dialog_video_generation',
          batch_id: batchId,
          generation_cost: dialogVideoGenerationCost,
          config_name: scenario?.name,
          scenario_name: game?.name
        });
        analyticsService.trackGoogleEvent('video_generation_modal_open', {
          batch_id: batchId,
          generation_cost: dialogVideoGenerationCost,
          config_name: scenario?.name,
          scenario_name: game?.name
        });
      } catch (error) {
        throw error;
      }
    };

    trackEvent();
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }



  const handleRefetchDialogData = async () => {
    try {
      const result = await getDialogByAlias({
        alias: alias as string,
        scenarioId: undefined,
        characterName: characterName,
        isAdminPanel: false
      }).unwrap();
      if (result?.batches) {
        setBatches(result.batches);
      }

    } catch (error) {
      const errorMessage = getErrorMessage(error);
      errorAlert(errorMessage);
    }
  };

  const handleVideoGeneration = async () => {
    if (tokenBalance < dialogVideoGenerationCost) {
      openBuyTokensModal();
      return;
    }

    try {
      TrackingAPI.trackEvent('video_generation_confirm', {
        batch_id: batchId,
        generation_cost: dialogVideoGenerationCost,
        config_name: scenario?.name,
        scenario_name: game?.name
      });
      analyticsService.trackGoogleEvent('video_generation_confirm', {
        batch_id: batchId,
        generation_cost: dialogVideoGenerationCost,
        config_name: scenario?.name,
        scenario_name: game?.name
      });
      await imageToVideo({ batchId });
      dispatch(refetchCurrentUser());
      handleRefetchDialogData();
      onClose();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      errorAlert(errorMessage);
    }
  };

  return (
    <>
      <NiceModal closeOnOutsideClick={false} onClose={onClose}>
        <Root>
          <TokenIcon />
          <Title>Confirm Token Usage</Title>
          <Subtitle>After confirming, tokens will be automatically deducted for future generations.</Subtitle>
          <Wrapper>
            <Button
              disabled={isLoading}
              onClick={handleVideoGeneration}
            >
              <ButtonContent>
                <span>Create Video</span>
                <TokenChip>
                  <TokenIcon />
                  <TokenCount>{dialogVideoGenerationCost}</TokenCount>
                </TokenChip>
              </ButtonContent>
            </Button>
          </Wrapper>
        </Root>
      </NiceModal>
      {isBuyTokensModalOpen && (
        <BuyTokensModal
          isOpen
          onClose={() => closeBuyTokensModal()}
          analyticsDataRenderReason='dialog_video_generation'
        />
      )}
    </>
  );
};

export default ConfirmVideoGenerationModal;

const Root = styled.div`
  width: 380px;
  padding: 24px;
  box-sizing: border-box;
  font-family: "Nunito Sans";
  font-optical-sizing: auto;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 410px) {
    width: unset;
  }

  svg {
    width: 48px;
    height: 48px;
  }
`;

const Wrapper = styled.div`
  font-family: 'Unbounded', sans-serif;
  font-optical-sizing: auto;
  width: 100%;
  display: flex;
  gap: 16px;
`;


const Title = styled.div`
  text-align: center;
  margin: 16px 0px 8px;
  color: #FFF;
  font-family: Raleway;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
`;

const Subtitle = styled.div`
  color: #FFF;
  margin-bottom: 24px;
  text-align: center;
  font-family: "Nunito Sans";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
`;



const Button = styled(UpgradeButton) <{ disabled?: boolean }>`
  margin-top: 0px;
  max-height: 46px;
  span {
  user-select: none;
  color: #FFF;
  text-align: center;
  font-family: "Nunito Sans";
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 12px;
`;

const TokenChip = styled.div`
  display: flex;
  align-items: center;
  border-radius: 500px;
  background: #0006;
  padding: 4px 8px;
  gap: 4px;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const TokenCount = styled.span`
  color: #FFF;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
`;
